import type { User } from "@qub/base-client";
import { useCallback, useEffect, useState } from "react";
import { AVATAR_BUCKET, qubbase } from "@/lib/qubbase";
import type { Profile } from "@/types";

/**
 * The signed-in user's row in public.profiles. Created on first sign-in with an upsert that ignores
 * duplicates (allowed by the "Users create their own profile" policy), then kept fresh via realtime.
 */
export function useProfile(user: User) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await qubbase
      .from<Profile>("profiles")
      .select("*")
      .eq("id", user.id)
      .single();
    if (error) setError(error.message);
    else setProfile(data);
  }, [user.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fallback =
        (user.user_metadata.username as string | undefined) ||
        user.email?.split("@")[0] ||
        `guest-${user.id.slice(0, 4)}`;
      const created = await qubbase
        .from<Profile>("profiles")
        .upsert(
          { id: user.id, username: fallback },
          { onConflict: "id", ignoreDuplicates: true },
        );
      if (created.error) return !cancelled && setError(created.error.message);
      if (!cancelled) await load();
    })();

    // Another tab (or device) changed the profile: pick it up.
    const channel = qubbase
      .channel(`profile-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          setProfile(payload.new as unknown as Profile);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void qubbase.removeChannel(channel);
    };
  }, [user, load]);

  const updateUsername = async (username: string) => {
    const { data, error } = await qubbase
      .from<Profile>("profiles")
      .update({ username, updated_at: new Date().toISOString() })
      .eq("id", user.id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    setProfile(data as unknown as Profile);
  };

  /** Uploads to avatars/<user id>/avatar-<time>.<ext> (storage policy: own folder only), then removes the old file. */
  const uploadAvatar = async (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const path = `${user.id}/avatar-${Date.now()}.${ext}`;
    const up = await qubbase.storage
      .from(AVATAR_BUCKET)
      .upload(path, file, { contentType: file.type, upsert: true });
    if (up.error) throw new Error(up.error.message);

    const previous = profile?.avatar_path;
    const { data, error } = await qubbase
      .from<Profile>("profiles")
      .update({ avatar_path: path, updated_at: new Date().toISOString() })
      .eq("id", user.id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    setProfile(data as unknown as Profile);
    if (previous && previous !== path)
      await qubbase.storage.from(AVATAR_BUCKET).remove([previous]);
  };

  const removeAvatar = async () => {
    if (!profile?.avatar_path) return;
    await qubbase.storage.from(AVATAR_BUCKET).remove([profile.avatar_path]);
    const { data, error } = await qubbase
      .from<Profile>("profiles")
      .update({ avatar_path: null })
      .eq("id", user.id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    setProfile(data as unknown as Profile);
  };

  return { profile, error, updateUsername, uploadAvatar, removeAvatar };
}
